import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { Alert, Badge, Button, Card } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import type { Identity } from '../lib/types';

/** Routes the signed-in user by identity state (tech design 3.1 branches). */
export function HomePage() {
  const { t } = useTranslation();
  const { identity, loading, refresh } = useSession();
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (loading) return <p className="text-slate-500">{t('app.loading')}</p>;
  if (!identity) return <Navigate to="/login" replace />;

  switch (identity.state) {
    case 'member':
      return <Dashboard identity={identity} />;
    case 'pending_invite': {
      const inv = identity.pendingInvites[0]!;
      const join = async () => {
        setJoining(true);
        setErr(null);
        try {
          await api(`/api/v1/me/join/${inv.teamId}`, { method: 'POST' });
          await refresh();
        } catch (e) {
          setErr(e instanceof ApiError ? e.message : String(e));
        } finally {
          setJoining(false);
        }
      };
      return (
        <StatePanel title={t('state.join.title')} body={t('state.join.body', { name: inv.orgName })}>
          {err && <Alert>{err}</Alert>}
          <Button onClick={() => void join()} disabled={joining}>
            {joining ? t('state.join.joining') : t('state.join.button', { name: inv.orgName })}
          </Button>
        </StatePanel>
      );
    }
    case 'org_pending':
      return (
        <StatePanel
          title={t('state.orgPending.title')}
          body={t('state.orgPending.body', { name: identity.org?.name })}
        />
      );
    case 'member_disabled':
      return (
        <StatePanel
          title={t('state.disabled.title')}
          body={t('state.disabled.body', { name: identity.org?.name })}
        />
      );
    case 'suspended':
      return (
        <StatePanel
          title={t('state.suspended.title')}
          body={t('state.suspended.body', { name: identity.org?.name })}
        />
      );
    default:
      if (identity.superAdmin) return <Navigate to="/superadmin" replace />;
      return (
        <StatePanel
          title={t('state.noOrg.title')}
          body={t('state.noOrg.body', { email: identity.user.email })}
        />
      );
  }
}

function StatePanel({ title, body, children }: { title: string; body: string; children?: React.ReactNode }) {
  return (
    <div className="mx-auto mt-10 max-w-lg space-y-4 rounded-xl border border-slate-200 bg-white p-8 text-center">
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-sm text-slate-600">{body}</p>
      {children}
    </div>
  );
}

function Dashboard({ identity }: { identity: Identity }) {
  const { t } = useTranslation();
  const org = identity.org!;
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">
        {t('dashboard.welcome', { name: identity.user.fullName ?? identity.user.email })}
      </h1>
      <div className="grid gap-4 md:grid-cols-2">
        <Card title={t('dashboard.org')}>
          <dl className="grid grid-cols-[9rem_1fr] gap-y-1 text-sm">
            <dt className="text-slate-500">{t('sa.name')}</dt>
            <dd>{org.name}</dd>
            <dt className="text-slate-500">{t('dashboard.slug')}</dt>
            <dd className="font-mono">{org.slug}</dd>
            <dt className="text-slate-500">{t('sa.status')}</dt>
            <dd>
              <Badge tone="green">{org.status}</Badge>
            </dd>
            <dt className="text-slate-500">{t('dashboard.team')}</dt>
            <dd className="font-mono text-xs">{identity.user.teamId}</dd>
            <dt className="text-slate-500">{t('dashboard.folder')}</dt>
            <dd className="font-mono text-xs">
              {org.evidenceRootDirectoryId ?? t('dashboard.folderMissing')}
            </dd>
          </dl>
        </Card>
        <Card title={t('dashboard.roles')}>
          <div className="flex flex-wrap gap-2">
            {identity.roles.map((r) => (
              <Badge key={r} tone="blue">
                {t(`roles.${r}`)}
              </Badge>
            ))}
          </div>
          <h3 className="mt-4 text-sm font-medium">{t('dashboard.nextSteps')}</h3>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm text-slate-600">
            <li>{t('dashboard.step1')}</li>
            <li>{t('dashboard.step2')}</li>
          </ol>
        </Card>
      </div>
    </div>
  );
}
